import { AxiosResponse } from 'axios';
import api from '../config/axiosConfig';
import { API_URLS } from '../config/Constants';

export interface ShareLinkData {
  shareId: string;
  shareUrl: string;
}

export interface SharedContent {
  shareId: string;
  contentType: 'season' | 'game' | 'tournament' | 'leaderboard';
  seasonId?: string;
  gameId?: string | null;
  shareType: 'teams' | 'players' | 'player';
  playerSeasonId?: string | null;
  clientId?: string;
  clientIds?: string[];
  name?: string;
  tournamentId?: string | null;
  entries?: { clientId: string; seasonId: string }[];
  client: {
    name: string;
    logo?: string;
  } | null;
  accessCount: number;
}

export interface ShareLink {
  _id: string;
  shareId: string;
  shareUrl: string;
  contentType: 'season' | 'game' | 'tournament' | 'leaderboard';
  seasonId?: string;
  gameId?: string | null;
  shareType: 'teams' | 'players';
  clientId?: string;
  clientIds?: string[];
  name?: string;
  entries?: { clientId: string; seasonId: string }[];
  createdBy: string;
  createdAt: string;
  accessCount: number;
  lastAccessedAt: string | null;
  isActive: boolean;
}

export const shareService = {
  // Helper for public API calls used by shared pages
  publicFetch: async (url: string): Promise<AxiosResponse<any>> => {
    const response = await api.get(url);

    return response;
  },

  // Generate a new share link
  generateShareLink: async (
    contentType: 'season' | 'game',
    resourceId: string,
    shareType: 'teams' | 'players' | 'player',
    playerSeasonId?: string,
  ): Promise<ShareLinkData> => {
    const response = await api.post(`${API_URLS.BASE_URL}/share/generate`, {
      contentType,
      resourceId,
      shareType,
      ...(playerSeasonId ? { playerSeasonId } : {}),
    });

    if (response.data.success) {
      return response.data.data;
    } else {
      throw new Error(response.data.message || 'Failed to generate share link');
    }
  },

  // Get shared content by share ID (public route)
  getSharedContent: async (shareId: string): Promise<SharedContent> => {
    // Use fetch directly to avoid auth interceptors for public endpoint
    const response = await api.get(`${API_URLS.BASE_URL}/share/shared/${shareId}`);

    return response.data.data;
  },

  // Get user's share links
  getMyShares: async (): Promise<ShareLink[]> => {
    const response = await api.get(`${API_URLS.BASE_URL}/share/my-shares`);

    if (response.data.success) {
      return response.data.data;
    } else {
      throw new Error(response.data.message || 'Failed to load shares');
    }
  },

  // Revoke a share link
  revokeShare: async (shareId: string): Promise<void> => {
    const response = await api.delete(`${API_URLS.BASE_URL}/share/${shareId}`);

    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to revoke share');
    }
  },

  // Copy link to clipboard
  copyToClipboard: async (url: string): Promise<boolean> => {
    try {
      await navigator.clipboard.writeText(url);
      return true;
    } catch (error) {
      // Fallback for older browsers
      try {
        const textArea = document.createElement('textarea');
        textArea.value = url;
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        const successful = document.execCommand('copy');
        document.body.removeChild(textArea);
        return successful;
      } catch (fallbackError) {
        console.error('Failed to copy to clipboard:', fallbackError);
        return false;
      }
    }
  },

  // Trigger native mobile share
  triggerNativeShare: async (url: string, title: string): Promise<boolean> => {
    if (navigator.share && typeof navigator.share === 'function') {
      try {
        await navigator.share({
          title: title,
          url: url,
          text: `Check out this ${title} from STATDOG`,
        });
        return true;
      } catch (error) {
        if ((error as Error).name !== 'AbortError') {
          console.error('Failed to share:', error);
        }
        return false;
      }
    }
    return false;
  },

  // Determine sharing method and execute
  handleShare: async (url: string, title: string): Promise<{ success: boolean; method: string }> => {
    // Check if mobile and native share is available
    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

    if (isMobile && navigator.share && typeof navigator.share === 'function') {
      const nativeSuccess = await shareService.triggerNativeShare(url, title);
      if (nativeSuccess) {
        return { success: true, method: 'native' };
      }
    }

    // Fallback to clipboard
    const clipboardSuccess = await shareService.copyToClipboard(url);
    return {
      success: clipboardSuccess,
      method: clipboardSuccess ? 'clipboard' : 'failed',
    };
  },
};
